import { useState, useEffect } from 'react';
import { Phone, Mail, MessageSquare, X } from 'lucide-react';
import { Button } from './button';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { reportPhoneConversion } from '@/utils/analytics';

export function StickyCTABar() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Give the hero and first services viewport room before showing the fixed CTA.
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > 2400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Expose an offset so pages can avoid being covered by the fixed CTA bar.
  useEffect(() => {
    const root = document.documentElement;
    if (!isVisible) {
      root.style.setProperty('--sticky-cta-offset', '0px');
      return;
    }

    // Approximate heights (mobile-first). Expanded state is taller.
    const px = isExpanded ? 240 : 120;
    root.style.setProperty('--sticky-cta-offset', `${px}px`);

    return () => {
      // On unmount, reset.
      root.style.setProperty('--sticky-cta-offset', '0px');
    };
  }, [isVisible, isExpanded]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[100] transition-all duration-300',
        isVisible ? 'translate-y-0' : 'translate-y-full opacity-0'
      )}
      role="region"
      aria-label="Contact options"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-t-lg bg-[#2b130f] shadow-2xl border border-b-0 border-white/20">
          <div className="absolute inset-0 rounded-t-lg bg-[linear-gradient(120deg,rgba(153,0,0,0.82),rgba(230,145,56,0.58)_58%,rgba(241,194,50,0.35))]" />
          <div className="absolute inset-0 rounded-t-lg opacity-20">
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.08),transparent_40%,rgba(255,255,255,0.08))]" />
          </div>

          <div className="relative px-4 py-3 sm:px-6 sm:py-4">
            {!isExpanded ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm sm:text-base md:text-lg lg:text-xl">
                    Need help with a system issue?
                  </p>
                  <p className="text-white/90 text-xs sm:text-sm md:text-base mt-1">
                    Inspections, monitoring, panel replacement, service calls, and quotes.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    className="bg-white text-[#990000] hover:bg-white/90 font-semibold text-xs sm:text-sm md:text-base shadow-sm transition-colors px-2 sm:px-3 md:px-4"
                    onClick={() => reportPhoneConversion()}
                    aria-label="Call (800) 540-2140"
                  >
                    <Phone className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1 sm:mr-2" aria-hidden="true" />
                    <span className="hidden sm:inline">Call Now</span>
                    <span className="sm:hidden">Call</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border border-white/80 text-white hover:bg-white hover:text-[#990000] font-semibold text-xs sm:text-sm md:text-base px-2 sm:px-3 md:px-4"
                    onClick={() => setIsExpanded(true)}
                    aria-label="Show more contact options"
                    aria-expanded={isExpanded}
                  >
                    <span className="hidden sm:inline">More Options</span>
                    <span className="sm:hidden">More</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-white font-semibold text-sm sm:text-base md:text-lg">
                    Choose the next step.
                  </p>
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="text-white/80 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent rounded"
                    aria-label="Close contact options"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    className="bg-white text-[#990000] hover:bg-white/90 font-semibold text-xs sm:text-sm shadow-sm transition-colors"
                    onClick={() => reportPhoneConversion()}
                    aria-label="Call (800) 540-2140"
                  >
                    <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" aria-hidden="true" />
                    <span className="hidden sm:inline">Call (800) 540-2140</span>
                    <span className="sm:hidden">Call</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="border border-white/80 text-white hover:bg-white hover:text-[#990000] font-semibold text-xs sm:text-sm"
                    onClick={() => {
                      navigate('/contact');
                      setIsExpanded(false);
                    }}
                    aria-label="Request a quote"
                  >
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" aria-hidden="true" />
                    <span className="hidden sm:inline">Request Quote</span>
                    <span className="sm:hidden">Quote</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="border border-white/80 text-white hover:bg-white hover:text-[#990000] font-semibold text-xs sm:text-sm"
                    onClick={() => {
                      navigate('/services');
                      setIsExpanded(false);
                    }}
                    aria-label="View our services"
                  >
                    <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" aria-hidden="true" />
                    <span className="hidden sm:inline">View Services</span>
                    <span className="sm:hidden">Services</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
